import type {
  ControlCommand,
  PeerInfo,
  RoomCredentials,
  SessionState,
  SessionTransport,
  TransportStatus,
  ViewRole,
} from "../types";
import {
  decodeMessage,
  validCommand,
  validState,
  validPosition,
  ROOM_RE,
  TOKEN_RE,
} from "../../server/protocol.mjs";
import { hasRememberedLocalDisplay, rememberLocalDisplay } from "./localPairing.ts";
import { clientIdentityForDocument } from "./clientIdentity.ts";
import type { AgentManagementResult, AgentRequest, AgentResult } from '../agent/types';
// A full-snapshot extension is tolerated by older displays/relays, whereas new
// keys inside their strictly validated settings or position packets are not.
type WireSessionState = SessionState & {
  cueAppearance?: { opacity: number; layer: "front" | "behind" };
};
const uid = () => crypto.randomUUID?.() || random();
const random = () => {
  const b = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...b))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
};
const encode = (buffer: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(buffer)));
const decode = (value: string) =>
  Uint8Array.from(atob(value), (c) => c.charCodeAt(0));
export function supportsLocalSynchronization(): boolean {
  return typeof crypto !== "undefined" && typeof crypto.subtle?.generateKey === "function";
}
async function localKeys() {
  const pair = await crypto.subtle.generateKey(
    { name: "ECDSA", namedCurve: "P-256" },
    true,
    ["sign", "verify"],
  );
  return {
    localControllerKey: encode(
      await crypto.subtle.exportKey("pkcs8", pair.privateKey),
    ),
    verificationKey: encode(
      await crypto.subtle.exportKey("spki", pair.publicKey),
    ),
  };
}
async function fallback(): Promise<RoomCredentials> {
  if (!supportsLocalSynchronization()) return {
    roomId: random().slice(0, 24), token: random(), controllerToken: random(),
  };
  const keys = await localKeys();
  return {
    roomId: random().slice(0, 24),
    token: random(),
    controllerToken: keys.localControllerKey,
    verificationKey: keys.verificationKey,
  };
}
/** Upgrade cached relay credentials without changing the room or its invitations. */
export async function ensureLocalCredentials(credentials: RoomCredentials): Promise<RoomCredentials> {
  if (!supportsLocalSynchronization() || !credentials.relayUrl || !credentials.controllerToken ||
    (credentials.verificationKey && credentials.localControllerKey)) return credentials;
  return { ...credentials, ...(await localKeys()) };
}
function baseUrl(url?: string) {
  return new URL(url || location.origin, location.origin).origin;
}
export async function createRoom(relayUrl?: string): Promise<RoomCredentials> {
  try {
    const base = baseUrl(relayUrl);
    const response = await fetch(`${base}/api/rooms`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
      signal: AbortSignal.timeout(3500),
    });
    if (!response.ok) throw new Error("Relay unavailable");
    const data = await response.json();
    if (
      typeof data.roomId !== "string" ||
      typeof data.token !== "string" ||
      typeof data.controllerToken !== "string"
    )
      throw new Error("Invalid relay response");
    return ensureLocalCredentials({ ...data, relayUrl: base });
  } catch (error) {
    if (relayUrl)
      throw new Error(
        `Could not create live room: ${error instanceof Error ? error.message : "connection failed"}`,
      );
    return fallback();
  }
}
export async function getNetworkUrls(
  credentials: RoomCredentials,
): Promise<{ urls: string[]; lanEnabled: boolean }> {
  if (!credentials.relayUrl) return { urls: [], lanEnabled: false };
  const response = await fetch(`${baseUrl(credentials.relayUrl)}/api/network`, {
    headers: {
      Authorization: `Bearer ${credentials.token}`,
      "X-Cue-Room": credentials.roomId,
    },
    signal: AbortSignal.timeout(3500),
  });
  if (!response.ok) throw new Error("Cannot discover LAN addresses");
  return response.json();
}
/** Credentials live in a fragment: never included in HTTP requests or Referer headers. Talent links omit controller capability. */
export function createViewUrl(
  credentials: RoomCredentials,
  role: ViewRole,
  origin = location.origin,
): string {
  const url = new URL(location.pathname, origin);
  url.searchParams.set("view", role);
  const c: RoomCredentials = {
    roomId: credentials.roomId,
    token: credentials.token,
    relayUrl: credentials.relayUrl,
    verificationKey: credentials.verificationKey,
  };
  if (role !== "talent") {
    c.controllerToken = credentials.controllerToken;
    c.localControllerKey = credentials.localControllerKey;
  }
  if (c.relayUrl && new URL(origin).origin !== location.origin) {
    const relayHost = new URL(c.relayUrl).hostname;
    if (
      ["localhost", "127.0.0.1", "[::1]"].includes(relayHost) ||
      relayHost === location.hostname
    )
      c.relayUrl = new URL(origin).origin;
  }
  url.hash = new URLSearchParams({ room: JSON.stringify(c) }).toString();
  return url.toString();
}
export function readViewCredentials(
  hash = location.hash,
): RoomCredentials | null {
  try {
    const value = new URLSearchParams(hash.replace(/^#/, "")).get("room");
    if (!value || value.length > 8000) return null;
    const c = JSON.parse(value);
    if (
      !c ||
      typeof c !== "object" ||
      !ROOM_RE.test(c.roomId || "") ||
      !TOKEN_RE.test(c.token || "")
    )
      return null;
    if (
      c.controllerToken !== undefined &&
      (typeof c.controllerToken !== "string" ||
        c.controllerToken.length > 2048 ||
        !/^[a-zA-Z0-9_+\/=-]+$/.test(c.controllerToken))
    )
      return null;
    if (
      c.verificationKey !== undefined &&
      (typeof c.verificationKey !== "string" ||
        c.verificationKey.length > 2048 ||
        !/^[a-zA-Z0-9+\/=]+$/.test(c.verificationKey))
    )
      return null;
    if (
      c.localControllerKey !== undefined &&
      (typeof c.localControllerKey !== "string" ||
        c.localControllerKey.length > 2048 ||
        !/^[a-zA-Z0-9+\/=]+$/.test(c.localControllerKey))
    ) return null;
    if (c.relayUrl !== undefined) {
      const relay = new URL(c.relayUrl);
      if (
        !["http:", "https:"].includes(relay.protocol) ||
        relay.username ||
        relay.password
      )
        return null;
      c.relayUrl = relay.origin;
    }
    return {
      roomId: c.roomId,
      token: c.token,
      controllerToken: c.controllerToken,
      verificationKey: c.verificationKey,
      localControllerKey: c.localControllerKey,
      relayUrl: c.relayUrl,
    };
  } catch {
    return null;
  }
}
const RECONCILE_MS = 3000;
const HEARTBEAT_TIMEOUT_MS = 12000;
const HANDSHAKE_TIMEOUT_MS = 8000;
const STATE_GAP_TIMEOUT_MS = 6000;
const LOCAL_PEER_TIMEOUT_MS = 16000;
const BUFFER_LIMIT = 2 * 1024 * 1024;
const revisionNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const sameCue = (a: SessionState, b: SessionState) =>
  a.visualCue === b.visualCue || JSON.stringify(a.visualCue ?? null) === JSON.stringify(b.visualCue ?? null);

export function createTransport(
  credentials: RoomCredentials,
  role: ViewRole,
  name: string = role,
): SessionTransport {
  const stateFns = new Set<(s: SessionState) => void>();
  const commandFns = new Set<(c: ControlCommand) => void>();
  const statusFns = new Set<(s: TransportStatus) => void>();
  let agentHandler: ((request: AgentRequest) => Promise<AgentResult>) | null = null;
  const agentDisconnectFns = new Set<() => void>();
  const agentPending = new Map<string, { resolve: (result: AgentManagementResult) => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> }>();
  const disconnectAgents = () => {
    for (const request of agentPending.values()) {
      clearTimeout(request.timer);
      request.reject(new Error('AI connection interrupted. Connect again when the live relay is ready.'));
    }
    agentPending.clear();
    for (const fn of agentDisconnectFns) fn();
  };
  let status: TransportStatus = {
    kind: credentials.relayUrl ? "relay" : "local",
    connected: false,
    peers: [],
    latency: null,
    error: null,
  };
  let closed = false;
  let latest: WireSessionState | null = null;
  let lastReceivedRevision = -1;
  let contentRevision: number | undefined;
  let socket: WebSocket | null = null;
  let reconnect: ReturnType<typeof setTimeout> | undefined;
  let handshake: ReturnType<typeof setTimeout> | undefined;
  let retries = 0;
  let ready = false;
  let denied = false;
  let lastPublished: SessionState | null = null;
  let lastLocalPublished: SessionState | null = null;
  let pendingSnapshot = false;
  let lastActivity = Date.now();
  let lastReconcile = 0;
  let lastSnapshotRequest = 0;
  let acknowledgedRevision = -1;
  let lastAcknowledgedAt = Date.now();
  let revisionOffset = 0;
  let lastSourceRevision = -1;
  let online = typeof navigator === "undefined" || navigator.onLine !== false;
  // Relay ownership survives this tab's reload. Signed local messages retain a
  // fresh sender so their replay-protection sequence can safely restart at zero.
  const localId = uid();
  const relayClientId = clientIdentityForDocument(credentials, role, () => localId);
  let localSequence = 0;
  const receivedSequences = new Map<string, number>();
  const localPeers = new Map<string, PeerInfo>();
  const localPeerAliases = new Map<string, string>();
  const localOwnerClaims = new Map<string, boolean>();
  let relayPeers: PeerInfo[] = [];
  let channel: BroadcastChannel | null = null;
  const hasLocalCrypto = supportsLocalSynchronization();
  let localError: string | null = !credentials.relayUrl && !hasLocalCrypto
    ? "Secure browser access is required for same-computer synchronization. Open HTTPS or connect a live relay."
    : null;
  let relayError: string | null = null;
  let localEnabled = false;
  let hadLocalDisplay = role === "operator" && hasRememberedLocalDisplay(credentials);
  let lastLocalAcknowledgement = 0;
  // Every new relay publisher must discover an existing owner before its first
  // local frame, including a tab opened while already offline. An established
  // owner retains its authority during an outage; a new tab cannot assume it.
  let relayOwnerAccepted = !credentials.relayUrl;
  let ownershipRejected = false;
  let ownershipNotice: string | null = null;
  let localOperatorId: string | null = null;
  let requiredRevision = -1;
  let stateGapSince: number | null = null;
  let stateStalled = false;
  let lastStateRecovery = 0;
  const startedAt = Date.now();
  const discoveryDeadline = startedAt + HANDSHAKE_TIMEOUT_MS;
  let authFailureGrace: ReturnType<typeof setTimeout> | undefined;
  let lastStatusSignature = "";
  let removeConnectivityListeners = () => {};
  // Speed step and recognition model are operator-only preferences. Older
  // displays/relays understand local speech and need its position, never its
  // model choice. Preserve their protocol without cloning settings per tick.
  const wireSettings = new WeakMap<object, Pick<WireSessionState, "settings" | "cueAppearance">>();
  const sanitizeState = (state: SessionState) => {
    let configuration = wireSettings.get(state.settings);
    if (!configuration) {
      const { speedStep: _speedStep, streamingModel: _streamingModel, cueOpacity, cueLayer, ...rest } = state.settings;
      configuration = {
        settings: { ...rest, speechEngine: rest.speechEngine === "streaming" ? "local" : rest.speechEngine } as SessionState["settings"],
        cueAppearance: { opacity: cueOpacity === undefined ? 1 : cueOpacity, layer: cueLayer === undefined ? "front" : cueLayer },
      };
      wireSettings.set(state.settings, configuration);
    }
    return { ...state, ...configuration };
  };
  const refreshStatus = () => {
    const localConnected = localEnabled && (role !== "operator" || relayOwnerAccepted) && (!credentials.relayUrl ||
      (role === "operator" ? localPeers.size > 0 : !!localOperatorId && localPeers.has(localOperatorId)));
    const retainLocalRoom = localEnabled && role === "operator" && !ownershipRejected &&
      denied && hadLocalDisplay && relayError === "Authentication failed";
    const peers = new Map<string, PeerInfo>();
    for (const peer of [...(ready ? relayPeers : []), ...[...localPeers.values()].filter((p) => p.role !== "operator" || p.id === localOperatorId)]) {
      const id = localPeerAliases.get(peer.id) || peer.id;
      if (id !== localId && id !== relayClientId) peers.set(id, id === peer.id ? peer : { ...peer, id });
    }
    status = {
      ...status,
      kind: ready ? "relay" : localConnected || retainLocalRoom ? "local" : credentials.relayUrl ? "relay" : "offline",
      connected: (ready || localConnected) && !stateStalled,
      peers: [...peers.values()],
      error: ownershipNotice || (localConnected && credentials.relayUrl && !ready
        ? "Live relay unavailable. Same-computer displays remain connected."
        : retainLocalRoom ? "Live relay unavailable. Waiting for a same-computer display. Start a new live room to reconnect other devices." : relayError || localError),
    };
    const signature = JSON.stringify(status);
    if (signature !== lastStatusSignature) {
      lastStatusSignature = signature;
      for (const fn of statusFns) fn(status);
    }
  };
  const sendWS = (message: unknown): boolean => {
    if (!ready || socket?.readyState !== WebSocket.OPEN || socket.bufferedAmount >= BUFFER_LIMIT) return false;
    try {
      socket.send(JSON.stringify(message));
      return true;
    } catch {
      return false;
    }
  };
  const requestSnapshot = () => {
    const now = Date.now();
    if (now - lastSnapshotRequest < 250) return;
    lastSnapshotRequest = now;
    sendWS({ type: "sync" });
    sendLocal({ type: "hello", revision: -1 });
  };
  const caughtUp = () => {
    if (!latest || latest.revision < requiredRevision) return;
    stateGapSince = null;
    stateStalled = false;
    if (relayError === "Display updates stalled. Reconnecting…") relayError = null;
  };
  const canonicalRevision = (revision: unknown) => {
    if (role === "operator" || !revisionNumber(revision)) return;
    requiredRevision = Math.max(requiredRevision, revision);
    if (!latest || latest.revision < requiredRevision) {
      stateGapSince ??= Date.now();
      requestSnapshot();
    } else caughtUp();
  };
  const accept = (state: WireSessionState, configuration?: unknown) => {
    if (!validState(state)) return false;
    if (state.revision > acknowledgedRevision) lastAcknowledgedAt = Date.now();
    acknowledgedRevision = Math.max(acknowledgedRevision, state.revision);
    // An operator owns its local working copy. A delayed relay echo must never
    // replace a newer unsent edit while its socket is congested or reconnecting.
    if (role === "operator") return false;
    if (state.revision < lastReceivedRevision) return false;
    // Fold the compatible wire extension into canonical settings. Old snapshots
    // without it retain their original opaque foreground cue presentation.
    // Already canonical movement keeps settings identity and cached geometry.
    const opacity = state.cueAppearance?.opacity ?? state.settings.cueOpacity ?? 1;
    const layer = state.cueAppearance?.layer ?? state.settings.cueLayer ?? "front";
    const settings = state.settings.cueOpacity === opacity && state.settings.cueLayer === layer
      ? state.settings : { ...state.settings, cueOpacity: opacity, cueLayer: layer };
    if (state.cueAppearance !== undefined) {
      const { cueAppearance: _appearance, ...snapshot } = state;
      state = { ...snapshot, settings };
    } else if (settings !== state.settings) state = { ...state, settings };
    const sameConfiguration = latest?.script.html === state.script.html &&
      JSON.stringify(latest.script) === JSON.stringify(state.script) &&
      JSON.stringify(latest.settings) === JSON.stringify(state.settings) && sameCue(latest, state);
    const duplicate = sameConfiguration && state.revision === lastReceivedRevision &&
      latest?.wordIndex === state.wordIndex && latest.wordProgress === state.wordProgress &&
      latest.playing === state.playing && latest.elapsed === state.elapsed &&
      latest.startedAt === state.startedAt && latest.countdownUntil === state.countdownUntil &&
      latest.blackout === state.blackout && latest.emergency === state.emergency;
    lastReceivedRevision = state.revision;
    contentRevision = revisionNumber(configuration) ? configuration : sameConfiguration ? contentRevision : undefined;
    latest = state;
    caughtUp();
    if (!duplicate) for (const fn of stateFns) fn(state);
    return true;
  };
  const acceptPosition = (position: unknown, configuration?: unknown) => {
    if (!validPosition(position)) return false;
    const movement = position as Omit<SessionState, "script" | "settings">;
    if (movement.revision > acknowledgedRevision) lastAcknowledgedAt = Date.now();
    acknowledgedRevision = Math.max(acknowledgedRevision, movement.revision);
    if (role === "operator") return false;
    if (!latest || (revisionNumber(configuration) && configuration !== contentRevision)) {
      canonicalRevision(movement.revision);
      requestSnapshot();
      return false;
    }
    if (movement.revision <= lastReceivedRevision) return false;
    lastReceivedRevision = movement.revision;
    latest = { ...latest, ...movement };
    caughtUp();
    for (const fn of stateFns) fn(latest);
    return true;
  };
  const rawPrivateKey = credentials.relayUrl ? credentials.localControllerKey : credentials.controllerToken;
  const privateKey = hasLocalCrypto && role !== "talent" && rawPrivateKey
    ? crypto.subtle.importKey("pkcs8", decode(rawPrivateKey), { name: "ECDSA", namedCurve: "P-256" }, false, ["sign"])
      .catch(() => { localError = "Invalid local controller credential."; refreshStatus(); return null; })
    : null;
  let publicKey: Promise<CryptoKey | null> | null = null;
  let currentVerificationKey = credentials.verificationKey;
  const importPublicKey = (value: string) => crypto.subtle
    .importKey("spki", decode(value), { name: "ECDSA", namedCurve: "P-256" }, false, ["verify"])
    .catch(() => { localError = "Invalid local display verification key."; refreshStatus(); return null; });
  if (hasLocalCrypto && currentVerificationKey) publicKey = importPublicKey(currentVerificationKey);
  let localSendQueue = Promise.resolve();
  const sendLocal = (message: Record<string, unknown>) => {
    const canSend = () => message.type === "bye" || (!closed && !(role === "operator" && ownershipRejected));
    if (!channel || !canSend()) return;
    const payload = {
      ...message,
      sequence: ++localSequence,
      sentAt: Date.now(),
      sender: localId,
      clientId: relayClientId,
      roomId: credentials.roomId,
      token: credentials.token,
      role,
      ownerAccepted: role === "operator" ? relayOwnerAccepted : undefined,
      name: name.slice(0, 80),
    };
    localSendQueue = localSendQueue.then(async () => {
      if (!canSend()) return;
      const key = privateKey ? await privateKey : null;
      if (role !== "talent" && !key) return;
      const signature = key ? encode(await crypto.subtle.sign(
        { name: "ECDSA", hash: "SHA-256" }, key,
        new TextEncoder().encode(JSON.stringify(payload)),
      )) : undefined;
      // Recheck after signing: ownership can be denied while crypto is pending.
      if (canSend()) channel?.postMessage({ payload, signature });
    }).catch(() => { localError = "Could not authenticate local synchronization."; refreshStatus(); });
  };
  const publishLocal = (force = false, recipient?: string) => {
    if (!latest || role !== "operator" || !localEnabled || !relayOwnerAccepted) return;
    const sameConfig = !force && lastLocalPublished?.script === latest.script && lastLocalPublished?.settings === latest.settings && sameCue(lastLocalPublished, latest);
    // Old clients reject unknown position fields. Cue changes travel in a full
    // snapshot and establish a new content generation; normal movement remains
    // compatible and cannot overtake a missing cue snapshot.
    const { script: _script, settings: _settings, visualCue: _cue, cueAppearance: _appearance, ...position } = latest;
    sendLocal(sameConfig
      ? { type: "position", position, contentRevision, recipient }
      : { type: "state", state: latest, contentRevision, recipient });
    lastLocalPublished = latest;
  };
  const discoverLocalOwner = () => {
    if (role !== "operator" || !localEnabled || relayOwnerAccepted || ownershipRejected ||
      Date.now() < discoveryDeadline) return;
    const operators = [...localPeers.values()].filter((peer) => peer.role === "operator");
    // Established owners (including older clients without this signed claim)
    // always take priority. Simultaneously restored candidates choose the same
    // smallest identity after discovery, instead of blocking each other forever.
    if (operators.some((peer) => localOwnerClaims.get(peer.id) !== false)) {
      ownershipNotice = "Another operator tab controls this room. Keep using it, or close it before taking over here.";
      return;
    }
    if (operators.some((peer) => peer.id < localId)) {
      ownershipNotice = "Another operator tab is starting this room. This tab will wait until it closes.";
      return;
    }
    ownershipNotice = null;
    // A waiting candidate may have an older working revision than the owner
    // that just left. Keep its working copy, but advance above the observed
    // canonical head so existing displays can accept the handoff.
    if (latest && acknowledgedRevision >= latest.revision) {
      const increase = acknowledgedRevision + 1 - latest.revision;
      revisionOffset += increase;
      latest = { ...latest, revision: latest.revision + increase };
      contentRevision = latest.revision;
    }
    relayOwnerAccepted = true;
    sendLocal({ type: "hello", revision: latest?.revision ?? -1, contentRevision });
    publishLocal(true);
  };
  const startLocal = () => {
    if (channel || !publicKey || (role !== "talent" && !privateKey) || typeof BroadcastChannel === "undefined") return;
    try {
      channel = new BroadcastChannel(`cue-room-${credentials.roomId}-${credentials.token}`);
      localEnabled = true;
      let receiveQueue = Promise.resolve();
      channel.onmessage = (event) => {
        receiveQueue = receiveQueue.then(async () => {
          if (closed) return;
          const message = event.data?.payload;
          if (!message || message.sender === localId || message.roomId !== credentials.roomId ||
            message.token !== credentials.token || !["operator", "talent", "remote"].includes(message.role) ||
            typeof message.sender !== "string" || typeof message.name !== "string" || message.name.length > 80 ||
            !Number.isSafeInteger(message.sequence) || message.sequence <= 0 ||
            typeof message.sentAt !== "number" || Math.abs(Date.now() - message.sentAt) > 60000) return;
          if (message.role !== "talent") {
            const key = publicKey ? await publicKey : null;
            if (!key || typeof event.data.signature !== "string" ||
              !(await crypto.subtle.verify({ name: "ECDSA", hash: "SHA-256" }, key,
                decode(event.data.signature), new TextEncoder().encode(JSON.stringify(message))))) return;
          }
          if (message.sequence <= (receivedSequences.get(message.sender) || 0)) return;
          receivedSequences.set(message.sender, message.sequence);
          localPeers.set(message.sender, { id: message.sender, role: message.role, name: message.name, lastSeen: Date.now() });
          // An alias only deduplicates the local and relay status rows. Sender
          // identities still govern signatures, replay protection and ownership.
          if (typeof message.clientId === "string" && /^[a-zA-Z0-9_-]{16,80}$/.test(message.clientId))
            localPeerAliases.set(message.sender, message.clientId);
          if (message.role === "operator") localOwnerClaims.set(message.sender, message.ownerAccepted !== false);
          if (message.role === "talent" && !hadLocalDisplay) {
            hadLocalDisplay = true;
            if (role === "operator") rememberLocalDisplay(credentials);
          }
          if (message.type === "bye") { localPeers.delete(message.sender); localPeerAliases.delete(message.sender); localOwnerClaims.delete(message.sender); }
          if (message.recipient && message.recipient !== localId) { refreshStatus(); return; }
          if (message.type === "state" && message.role === "operator" && accept(message.state, message.contentRevision)) localOperatorId = message.sender;
          if (message.type === "position" && message.role === "operator" && acceptPosition(message.position, message.contentRevision)) localOperatorId = message.sender;
          if (message.type === "command" && role === "operator" && relayOwnerAccepted && message.role !== "talent" && validCommand(message.command))
            for (const fn of commandFns) fn(message.command);
          if (message.type === "hello" && role === "operator" && latest &&
            (message.revision !== latest.revision || message.contentRevision !== contentRevision)) publishLocal(true, message.sender);
          // Respond to incoming traffic as well as the timer. Background-tab
          // timer throttling must not make an actively receiving display vanish.
          if (role !== "operator" && message.role === "operator" &&
            ["hello", "state", "position"].includes(message.type) &&
            Date.now() - lastLocalAcknowledgement >= RECONCILE_MS) {
            lastLocalAcknowledgement = Date.now();
            sendLocal({ type: "hello", revision: latest?.revision ?? -1, contentRevision });
          }
          refreshStatus();
        }).catch(() => {});
      };
      sendLocal({ type: "hello", revision: latest?.revision ?? -1, contentRevision });
      publishLocal(true);
      refreshStatus();
    } catch {
      localError = "This browser cannot synchronize same-computer windows.";
      refreshStatus();
    }
  };
  const publishRelay = (force = false) => {
    if (role !== "operator" || !latest) return;
    const sameConfig = !force && !pendingSnapshot && lastPublished?.script === latest.script && lastPublished?.settings === latest.settings && sameCue(lastPublished, latest);
    const { script: _script, settings: _settings, visualCue: _cue, cueAppearance: _appearance, ...position } = latest;
    const message = sameConfig
      ? { type: "position", position, contentRevision }
      : { type: "state", state: latest, contentRevision };
    // Never advance the delta baseline for a frame that was not enqueued.
    if (sendWS(message)) { lastPublished = latest; pendingSnapshot = false; }
    else pendingSnapshot = true;
  };
  const invalidateSocket = () => {
    clearTimeout(handshake);
    const stale = socket;
    if (stale) disconnectAgents();
    socket = null;
    ready = false;
    relayPeers = [];
    try { stale?.close(); } catch {}
  };
  const reconnectSoon = (delay = 500) => {
    if (closed || denied || !online || !credentials.relayUrl) return;
    clearTimeout(reconnect);
    reconnect = setTimeout(connect, delay);
  };
  const syncNow = () => {
    if (closed) return;
    sendLocal({ type: "hello", revision: latest?.revision ?? -1, contentRevision });
    if (!ready) return;
    if (role === "operator" && latest && (pendingSnapshot ||
      (acknowledgedRevision < latest.revision && Date.now() - lastAcknowledgedAt >= RECONCILE_MS))) publishRelay(true);
    // Conditional snapshots are tiny acknowledgements when caught up. Older
    // relays ignore these fields and return a compatible complete snapshot.
    sendWS({ type: "sync", revision: latest?.revision ?? -1, contentRevision });
    sendWS({ type: "ping", at: Date.now() });
  };
  const connect = () => {
    if (closed || !online || denied || !credentials.relayUrl) return;
    clearTimeout(reconnect);
    invalidateSocket();
    lastPublished = null;
    pendingSnapshot = true;
    lastActivity = Date.now();
    const url = new URL("/ws", credentials.relayUrl);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    let ws: WebSocket;
    try { ws = new WebSocket(url.toString()); }
    catch { relayError = "Cannot reach room relay. Reconnecting…"; refreshStatus(); reconnectSoon(1000); return; }
    socket = ws;
    handshake = setTimeout(() => {
      if (closed || socket !== ws || ready) return;
      invalidateSocket();
      discoverLocalOwner();
      relayError = "Relay handshake timed out. Reconnecting…";
      refreshStatus();
      reconnectSoon();
    }, HANDSHAKE_TIMEOUT_MS);
    ws.onopen = () => {
      if (ws !== socket || closed) return;
      ws.send(JSON.stringify({ type: "auth", clientId: relayClientId, roomId: credentials.roomId,
        token: credentials.token, controllerToken: role === "talent" ? undefined : credentials.controllerToken,
        role, name: name.slice(0, 80), verificationKey: role === "operator" ? currentVerificationKey : undefined }));
    };
    ws.onmessage = (event) => {
      if (ws !== socket || closed) return;
      const message = decodeMessage(event.data);
      if (!message) return;
      lastActivity = Date.now();
      // AI requests travel only on the authenticated owner socket. They are
      // never echoed to BroadcastChannel, talent screens or phone remotes.
      if (role === 'operator' && message.type === 'agent-managed') {
        const pending = agentPending.get(message.requestId);
        if (pending) {
          agentPending.delete(message.requestId);
          clearTimeout(pending.timer);
          if (message.ok === true && message.result && typeof message.result === 'object') pending.resolve(message.result);
          else pending.reject(new Error(message.error?.message || 'AI connection could not be updated.'));
        }
        return;
      }
      if (role === 'operator' && message.type === 'agent-request') {
        if (typeof message.requestId !== 'string' || message.requestId.length > 100 ||
            typeof message.deadline !== 'number' || message.deadline < Date.now() ||
            message.deadline > Date.now() + 60000) return;
        const handler = agentHandler;
        void (handler ? handler(message as AgentRequest) : Promise.resolve<AgentResult>({ ok: false, error: { code: 'NOT_ENABLED', message: 'AI access is not enabled in this operator window.' } }))
          .catch((): AgentResult => ({ ok: false, error: { code: 'OPERATOR_ERROR', message: 'The operator could not complete the request. Check the workspace before retrying.' } }))
          .then(result => {
            if (socket !== ws || closed || !ready) return;
            const reply = { ...result, type: 'agent-result', requestId: message.requestId };
            if (new TextEncoder().encode(JSON.stringify(reply)).length > 900000) {
              sendWS({ type: 'agent-result', requestId: message.requestId, ok: false, error: { code: 'RESULT_TOO_LARGE', message: 'Request a smaller page or a single script.' } });
            } else sendWS(reply);
          });
        return;
      }
      if (message.type === "ready") {
        clearTimeout(handshake);
        ready = true;
        retries = 0;
        if (!stateStalled) relayError = null;
        ownershipNotice = null;
        relayOwnerAccepted = true;
        if (role === "operator" && latest && revisionNumber(message.revision) && message.revision >= latest.revision) {
          const increase = message.revision + 1 - latest.revision;
          revisionOffset += increase;
          latest = { ...latest, revision: latest.revision + increase };
          contentRevision = latest.revision;
        }
        publishRelay(true);
        publishLocal(true);
        syncNow();
        refreshStatus();
      }
      if (hasLocalCrypto && message.type === "local-key" && typeof message.verificationKey === "string" &&
        message.verificationKey.length <= 2048 && /^[a-zA-Z0-9+/=]+$/.test(message.verificationKey) && role === "talent") {
        // The authenticated relay vouches for the operator's public key. Viewer
        // invitations never carry a controller credential or signing key.
        currentVerificationKey = message.verificationKey;
        publicKey = importPublicKey(currentVerificationKey);
        startLocal();
      }
      if (message.type === "state") accept(message.state, message.contentRevision);
      if (message.type === "position") acceptPosition(message.position, message.contentRevision);
      if (message.type === "synced" && revisionNumber(message.revision)) {
        if (message.revision > acknowledgedRevision) lastAcknowledgedAt = Date.now();
        acknowledgedRevision = Math.max(acknowledgedRevision, message.revision);
        canonicalRevision(message.revision);
        if (role !== "operator" && (message.revision !== latest?.revision ||
          (revisionNumber(message.contentRevision) && message.contentRevision !== contentRevision))) requestSnapshot();
      }
      if (message.type === "resync") publishRelay(true);
      if (message.type === "command" && role === "operator" && validCommand(message.command))
        for (const fn of commandFns) fn(message.command);
      if (message.type === "peers" && Array.isArray(message.peers)) { relayPeers = message.peers; refreshStatus(); }
      if (message.type === "pong" && typeof message.at === "number") {
        canonicalRevision(message.revision);
        status = { ...status, latency: Math.max(0, Date.now() - message.at) };
        refreshStatus();
      }
    };
    ws.onclose = (event) => {
      if (ws !== socket || closed) return;
      invalidateSocket();
      denied = event.code === 1008;
      if (denied && event.reason === "Room already has an operator") {
        ownershipRejected = true;
        relayOwnerAccepted = false;
        // Retire this identity immediately. Continuing hello messages would
        // falsely block a legitimate operator's later offline recovery.
        sendLocal({ type: "bye" });
      }
      if (denied && event.reason === "Authentication failed" && role === "operator" && localEnabled) {
        // A relay restart must not rotate the room before the local display has
        // had a chance to announce itself. Rotation would strand its open URL.
        discoverLocalOwner();
        relayError = "Live room unavailable. Checking same-computer displays…";
        syncNow();
        publishLocal(true);
        refreshStatus();
        clearTimeout(authFailureGrace);
        authFailureGrace = setTimeout(() => {
          if (closed) return;
          discoverLocalOwner();
          relayError = "Authentication failed";
          refreshStatus();
        }, Math.max(1000, relayOwnerAccepted ? 0 : discoveryDeadline - Date.now()));
        return;
      }
      relayError = denied ? event.reason || "Room authorization failed." : "Connection interrupted. Reconnecting…";
      refreshStatus();
      if (!denied) reconnectSoon(Math.min(15000, 500 * 2 ** Math.min(retries++, 5)) + Math.random() * 250);
    };
    ws.onerror = () => {
      if (ws === socket && !closed) { relayError = "Cannot reach room relay."; refreshStatus(); }
    };
  };
  startLocal();
  if (credentials.relayUrl) connect();
  const interval = setInterval(() => {
    if (closed) return;
    const now = Date.now();
    for (const [id, peer] of localPeers) if (now - peer.lastSeen > LOCAL_PEER_TIMEOUT_MS) {
      localPeers.delete(id);
      localPeerAliases.delete(id);
      localOwnerClaims.delete(id);
    }
    // navigator.onLine only describes the network adapter. Repeated immediate
    // relay failures must not reset the local startup grace forever on Wi-Fi
    // without internet. A confirmed ownership rejection can never use this.
    if (!ready) discoverLocalOwner();
    if (ready && now - lastActivity > HEARTBEAT_TIMEOUT_MS) {
      invalidateSocket();
      relayError = "Relay heartbeat lost. Reconnecting…";
      reconnectSoon();
    }
    if (ready && stateGapSince !== null && now - stateGapSince >= STATE_GAP_TIMEOUT_MS &&
      now - lastStateRecovery >= STATE_GAP_TIMEOUT_MS) {
      lastStateRecovery = now;
      stateStalled = true;
      invalidateSocket();
      relayError = "Display updates stalled. Reconnecting…";
      reconnectSoon();
    }
    if (pendingSnapshot && ready) publishRelay(true);
    if (now - lastReconcile >= RECONCILE_MS) { lastReconcile = now; syncNow(); }
    refreshStatus();
  }, 500);
  if (typeof window !== "undefined") {
    const offline = () => {
      online = false;
      clearTimeout(reconnect);
      invalidateSocket();
      discoverLocalOwner();
      relayError = "Network offline. Waiting to reconnect.";
      syncNow();
      refreshStatus();
    };
    const resume = () => {
      if (closed) return;
      online = typeof navigator === "undefined" || navigator.onLine !== false;
      if (!online) return;
      if (credentials.relayUrl && !denied && (!ready || Date.now() - lastActivity > HEARTBEAT_TIMEOUT_MS)) connect();
      syncNow();
    };
    const visible = () => { if (document.visibilityState === "visible") resume(); };
    window.addEventListener("offline", offline);
    window.addEventListener("online", resume);
    window.addEventListener("focus", resume);
    window.addEventListener("pageshow", resume);
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", visible);
    removeConnectivityListeners = () => {
      window.removeEventListener("offline", offline);
      window.removeEventListener("online", resume);
      window.removeEventListener("focus", resume);
      window.removeEventListener("pageshow", resume);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", visible);
    };
  }
  return {
    agentManage(request) {
      if (role !== 'operator' || closed || !ready) return Promise.reject(new Error('A live relay connection is required to connect an AI client.'));
      if (agentPending.size >= 4) return Promise.reject(new Error('Wait for the current connection request to finish.'));
      const requestId = uid();
      return new Promise<AgentManagementResult>((resolve, reject) => {
        const timer = setTimeout(() => {
          agentPending.delete(requestId);
          reject(new Error('Connection request timed out. Check active connections before trying again.'));
        }, 10000);
        agentPending.set(requestId, { resolve, reject, timer });
        if (!sendWS({ ...request, type: 'agent-manage', requestId })) {
          clearTimeout(timer);
          agentPending.delete(requestId);
          reject(new Error('The live relay is unavailable. Try again after it reconnects.'));
        }
      });
    },
    onAgentRequest(fn) { agentHandler = fn; return () => { if (agentHandler === fn) agentHandler = null; }; },
    onAgentDisconnect(fn) { agentDisconnectFns.add(fn); return () => agentDisconnectFns.delete(fn); },
    publish(state) {
      if (closed || role !== "operator" || state.revision < lastSourceRevision) return;
      const clean = sanitizeState(state);
      if (!validState(clean)) {
        relayError = "This script cannot be synchronized. Check its size and formatting, then try again.";
        refreshStatus();
        return;
      }
      const next = revisionOffset ? { ...clean, revision: clean.revision + revisionOffset } : clean;
      if (!latest || latest.script !== next.script || latest.settings !== next.settings || !sameCue(latest, next)) contentRevision = next.revision;
      lastSourceRevision = state.revision;
      latest = next;
      publishLocal();
      if (credentials.relayUrl) publishRelay();
    },
    command(command) {
      if (closed || role === "talent" || !validCommand(command)) return;
      if (role === "operator") { for (const fn of commandFns) fn(command); return; }
      // Pick one route per command: sending on both would apply relative moves
      // and play/pause toggles twice. Local transport survives internet outages.
      if (localEnabled && localOperatorId && localPeers.has(localOperatorId))
        sendLocal({ type: "command", command });
      else if (credentials.relayUrl) sendWS({ type: "command", command });
      else sendLocal({ type: "command", command });
    },
    subscribe(fn) { stateFns.add(fn); if (latest) fn(latest); return () => stateFns.delete(fn); },
    onCommand(fn) { commandFns.add(fn); return () => commandFns.delete(fn); },
    onStatus(fn) { statusFns.add(fn); fn(status); return () => statusFns.delete(fn); },
    close() {
      if (closed) return;
      sendLocal({ type: "bye" });
      closed = true;
      disconnectAgents();
      removeConnectivityListeners();
      clearTimeout(reconnect);
      clearTimeout(handshake);
      clearTimeout(authFailureGrace);
      clearInterval(interval);
      void localSendQueue.finally(() => channel?.close());
      try { socket?.close(1000, "View closed"); } catch {}
      stateFns.clear(); commandFns.clear(); statusFns.clear();
      agentHandler = null; agentDisconnectFns.clear();
    },
  };
}
